import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ModuleBaseModel, ModuleBasePropsModel, ScrapbookContentType } from '@/types';
import { MetadataContext, ModuleBase } from '@/components';
import { Color, Palette } from '@/types/ColorLibraryModel';
import { loadUserPalettes } from '@/api/color-library';
import ColourStripColour from './ColourStripColour';
import ColourStripDetails from './ColourStripDetails';

import { SaveToScrapbookButton } from '@/components/scrapbook';

import { generateHashCode } from '@/utils/scrapbookUtils';

type ColourStripModel = ModuleBaseModel<
  {
    colours: Color[];
    numberOfColoursPerRow: number;
  } & ModuleBasePropsModel
>;

const ColourStrip = (data: ColourStripModel) => {
  const {
    props: { colours, numberOfColoursPerRow },
  } = data;

  const [selectedColor, setSelectedColor] = useState<Color | null>(null);

  const [palettes, setPalettes] = useState<Palette[]>([]);

  const metadataContext = useContext(MetadataContext);

  useEffect(() => {
    loadUserPalettes().then(({ result }) => setPalettes(result));
  }, []);

  const colorRows = useMemo(() => {
    return colours.reduce((resultArray: Color[][], item, index) => {
      const chunkIndex = Math.floor(index / numberOfColoursPerRow);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);
  }, [colours, numberOfColoursPerRow]);

  const scrapbookPaletteHashText = useMemo(
    () =>
      generateHashCode(
        colours
          .map(function (elem) {
            return elem.id;
          })
          .join(','),
      ),
    [colours],
  );

  return (
    <ModuleBase data={data} noModuleBaseColor>
      <div className="group relative container">
        {colorRows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex w-full flex-col">
            <div className="flex flex-wrap">
              {row.map((color) => (
                <ColourStripColour
                  color={color}
                  key={color.id}
                  isSelected={selectedColor?.id === color.id}
                  onClick={setSelectedColor}
                />
              ))}
            </div>
            {selectedColor && row.includes(selectedColor) && (
              <ColourStripDetails color={selectedColor} palettes={palettes} onClose={() => setSelectedColor(null)} />
            )}
          </div>
        ))}

        <div className="absolute -bottom-4 left-5 transition-opacity duration-300 group-hover:opacity-100 pointer-fine:opacity-0">
          <SaveToScrapbookButton
            scrapbookContent={{
              pageId: metadataContext.pageId,
              pageTitle: metadataContext.pageTitle,
              pageUrl: metadataContext.pageUrl,
              id: metadataContext.pageId,
              name: metadataContext.pageTitle,
              colours: colours,
            }}
            scrapbookContentType={ScrapbookContentType.ColourPalette}
            scrapbookPath={`colourPalette.${metadataContext.pageId}.${scrapbookPaletteHashText}.json`}
            successMessage="Color palette was added to scrapbook successfully"
            tooltipProps={{ content: 'Save palette to Scrapbook' }}
            className="btn icon !p-1.5"
          />
        </div>
      </div>
    </ModuleBase>
  );
};

export default ColourStrip;
